import { Content, Item, ItemText, Portal, Root, Trigger, Value, Viewport } from '@radix-ui/react-select';
import clsx from 'clsx';
import type { FunctionComponent, ReactNode } from 'react';

import type { ButtonProps } from '../../core/button/Button';
import { Button } from '../../core/button/Button';
import { Card } from '../../core/card/Card';
import { useColorSchemeScope } from '../../core/color-scheme/ColorSchemeScope';
import { IconChevronGrabberVertical } from '../../core/icons';
import { floatingSurfaceClassNames } from '../../core/surface/surfaceClasses';

export type VersionSelect = {
    readonly id: string;
    readonly author: string;
    readonly badge: ReactNode;
    readonly title: string;
};

type VersionSelectProps = {
    readonly ariaLabel: string;
    readonly contentInset?: 'default' | 'none' | 'table';
    readonly currentVersion?: VersionSelect;
    readonly currentVersionDescriptionActionLabel?: string;
    readonly currentVersionDescription?: string;
    readonly disabled?: boolean;
    readonly onSwitchVersion: (id: string) => void;
    readonly onCurrentVersionDescriptionAction?: () => void;
    readonly placeholder?: string;
    readonly variant?: ButtonProps['variant'];
    readonly versions: readonly VersionSelect[];
};

export const VersionSelect: FunctionComponent<VersionSelectProps> = ({
    ariaLabel,
    contentInset = 'default',
    currentVersion,
    currentVersionDescription,
    currentVersionDescriptionActionLabel,
    disabled = false,
    onCurrentVersionDescriptionAction,
    onSwitchVersion,
    placeholder = 'Select Version',
    variant = 'tertiary',
    versions,
}) => {
    const colorScheme = useColorSchemeScope();
    const contentInsetClassName =
        contentInset === 'table'
            ? 'px-[calc(var(--spacing-s)+var(--spacing-m))]'
            : contentInset === 'none'
              ? ''
              : 'px-l';

    return (
        <Root value={currentVersion?.id} onValueChange={onSwitchVersion}>
            <div
                className={clsx(
                    'flex w-full min-w-0 flex-col rounded-lg font-sans',
                    currentVersionDescription !== undefined && 'transition-[background-color] duration-200 ease-in-out',
                    currentVersionDescription !== undefined && !disabled && 'hover:bg-surface-hover',
                )}
            >
                <Trigger asChild={true} data-testid="version-select" disabled={disabled}>
                    <Button
                        aria-label={ariaLabel}
                        className={clsx(
                            'min-w-0 justify-start',
                            currentVersionDescription !== undefined && contentInsetClassName,
                            currentVersionDescription !== undefined ? '!pb-xxs !pt-s' : '',
                        )}
                        disabled={disabled}
                        type="button"
                        variant={variant}
                    >
                        <span className="min-w-0 truncate font-sans text-body-s font-medium tracking-tight">
                            <Value placeholder={placeholder}>{currentVersion?.title ?? placeholder}</Value>
                        </span>
                        <span
                            className="flex h-[16px] w-[16px] shrink-0 text-text-content-subtle transition-transform duration-200 ease-in-out [&>svg]:h-[16px] [&>svg]:w-[16px]"
                            data-testid="selected-version"
                        >
                            <IconChevronGrabberVertical />
                        </span>
                    </Button>
                </Trigger>
                {currentVersionDescription !== undefined ? (
                    onCurrentVersionDescriptionAction === undefined ? (
                        <div
                            className={clsx(
                                'line-clamp-2 max-w-[28rem] pb-s text-left text-body-xs font-normal text-text-muted',
                                contentInsetClassName,
                            )}
                        >
                            {currentVersionDescription}
                        </div>
                    ) : (
                        <button
                            aria-label={currentVersionDescriptionActionLabel}
                            className={clsx(
                                'group flex max-w-[28rem] items-start gap-xs border-none bg-transparent pb-s text-left font-sans',
                                'cursor-pointer text-body-xs font-normal text-text-muted hover:text-text-primary',
                                'focus-visible:outline focus-visible:outline-1 focus-visible:outline-focus-ring',
                                contentInsetClassName,
                            )}
                            type="button"
                            onClick={onCurrentVersionDescriptionAction}
                        >
                            <span className="line-clamp-2 min-w-0 flex-1 text-pretty">{currentVersionDescription}</span>
                        </button>
                    )
                ) : null}
            </div>
            <Portal>
                <Content
                    className={clsx(
                        floatingSurfaceClassNames.tone.gray,
                        'font-sans rounded-xl overflow-hidden w-[16rem] z-99999',
                    )}
                    data-color-scheme={colorScheme}
                    position="popper"
                    style={{ maxHeight: 'var(--radix-select-content-available-height)' }}
                >
                    <Viewport className="bg-surface-raised p-xs">
                        {versions.map((version) => (
                            <Item key={version.id} asChild={true} value={version.id}>
                                <Card
                                    className={clsx(
                                        'box-border w-full mb-xs last:mb-0',
                                        'data-highlighted:outline data-highlighted:outline-outline-selected-subtle',
                                    )}
                                    isSelected={version.id === currentVersion?.id}
                                >
                                    <div className="flex items-center gap-s justify-between text-left text-text-content-strong text-body-s font-medium">
                                        <ItemText>{version.title}</ItemText>
                                        {version.badge}
                                    </div>
                                    <div className="text-text-content-subtle text-body-xxs font-normal leading-[1.25rem]">
                                        {version.author}
                                    </div>
                                </Card>
                            </Item>
                        ))}
                    </Viewport>
                </Content>
            </Portal>
        </Root>
    );
};
